import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as omitBy from 'lodash/omitBy'
import { change, Field, FieldArray, reduxForm } from 'redux-form'
import * as formStyles from 'styles/molecules/forms/OnboardingForm.css'
import * as styles from 'components/features/pfm/PropertyForm/PropertyForm.css'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import Button from 'components/features/pfm/Button/Button'
import HomesFormFields from 'components/molecules/forms/HomesFormFields'
import CarsFormFields from 'components/molecules/forms/CarsFormFields'
import PropertyList, { PropertyListOwnProps } from 'components/molecules/forms/PropertyList'
import { ReduxFormProps, OnSubmitReduxForm } from 'interfaces/component'
import { HomeFormValues, CarFormValues, Car, HousingType } from 'interfaces/property'
import schema from 'schemas/propertySchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import { FIELD_CURRENCY_WRAPPED_PROPS, FIELD_BIRTH_DATE_PROPS, FIELD_ZIP_PROPS } from 'constants/fields'
import { FORM_ONBOARDING_PROPERTY, HOME_FORM_INITIAL_VALUES, CAR_FORM_INITIAL_VALUES } from 'constants/forms'
import RadioButtonList from 'components/atoms/inputs/RadioButtonList'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { onFormSubmit } from 'helpers/formHelpers'
import SelectWrapped from 'components/atoms/inputs/OldSelectWrapped'
import { HOUSING_TYPES } from 'constants/member'
import { formatHousingType } from 'helpers/formatHelpers'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { ONBOARDING_SUMMARY } from 'constants/pages'
import CardLayout from 'components/layouts/CardLayout'

export type YesNoValue = 'yes' | 'no'

type PropertyFormBaseValues = {
  birth_date: string
  income: number | null
  zip5: string | null
  car_owner: YesNoValue
  homes: HomeFormValues[]
  cars: CarFormValues[]
}

export type PropertyFormValues = PropertyFormBaseValues & {
  housing_type: HousingType | null
}

export type ValidatedPropertyFormValues = PropertyFormBaseValues & {
  housing_type: HousingType
}

export type PropertyFormStateProps = {
  housing_type: HousingType
  car_owner: YesNoValue
  alreadySubmitted: boolean
  initialValues: PropertyFormValues
  preloadedCars: Car[]
}

export type PropertyFormDispatchProps = {
  getCarMakes: () => void
  getCarModels: (makeId: number) => void

  onCarAdded: (idx: number) => void
  onHomeAdded: (idx: number) => void

  change: typeof change
}

export type PropertyFormOwnProps = {
  mode?: 'regular' | 'credit-card'
}

type PropertyFormProps =
  & PropertyFormStateProps
  & PropertyFormDispatchProps
  & PropertyFormOwnProps
  & ReduxFormProps

const FIELDS_ARRAY_CARS_PROPS: PropertyListOwnProps<CarFormValues> = {
  addLabel: 'Add another car',
  child: CarsFormFields,
  initialValues: CAR_FORM_INITIAL_VALUES,
}

const FIELDS_ARRAY_HOMES_PROPS: PropertyListOwnProps<HomeFormValues> = {
  addLabel: 'Add another home',
  child: HomesFormFields,
  initialValues: HOME_FORM_INITIAL_VALUES,
}

export const FIELD_INCOME_PROPS = {
  label: 'Annual Income',
  type: 'tel',
  mask: createNumberMask({ prefix: '$' }),
  maxLength: 11,
  qaMarker: 'income-input',
}

export const FIELD_HOUSING_TYPE_PROPS = {
  label: 'Housing Type',
  options: HOUSING_TYPES.map((type) => ({
    label: formatHousingType(type),
    value: type,
  })),
  theme: 'white',
  hint: 'Your housing type selection helps determine your peer group. If you own a home, we recommend you select that option.',
  qaMarker: 'housing-type',
}

type YesNoItem = {
  label: string
  value: YesNoValue
}
const YES_NO_ITEMS: YesNoItem[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

@reduxForm({
  form: FORM_ONBOARDING_PROPERTY,
  asyncValidate: asyncValidator(schema),
  shouldAsyncValidate,
})
class PropertyForm extends Component<PropertyFormProps, {}> {

  render () {
    const {
      handleSubmit,
      car_owner,
      onCarAdded,
    } = this.props

    const carsForm = car_owner === 'yes' ? (
      <div className={ styles.innerForm }>
        <FieldArray
          name='cars'
          component={ PropertyList }
          props={{
            ...FIELDS_ARRAY_CARS_PROPS,
            onFieldAdded: onCarAdded,
            qaMarker: 'add-car-button',
          }}
        />
      </div>
    ) : null

    return (
      <form
        className={ formStyles.onboardingForm }
        onSubmit={ handleSubmit ? handleSubmit(this.onSubmit) : undefined }
      >
        <CardLayout className={ formStyles.cardLayout }>
          <ul className={ formStyles.controlList }>
            <li className={ formStyles.controlListItem }>
              <Field
                name='birth_date'
                props={{
                  ...FIELD_BIRTH_DATE_PROPS,
                  autoFocus: true,
                }}
                component={ TextFieldWrapped }
              />
            </li>
            <li className={ formStyles.controlListItem }>
              <Field
                name='income'
                props={ FIELD_INCOME_PROPS }
                component={ TextFieldWrapped }
                { ...FIELD_CURRENCY_WRAPPED_PROPS }
              />
            </li>
            <li className={ formStyles.controlListItem }>
              <Field
                name='housing_type'
                props={ FIELD_HOUSING_TYPE_PROPS }
                component={ SelectWrapped }
              />
              { this.getHousingTypeFields() }
            </li>
            <li className={ formStyles.rowButtonListItem }>
              <Field
                name='car_owner'
                props={{
                  name: 'car_owner',
                  label: 'Do you own a car?',
                  items: YES_NO_ITEMS,
                  qaMarker: 'car-owner',
                }}
                component={ RadioButtonList }
              />
              { carsForm }
            </li>
          </ul>
        </CardLayout>
        { this.renderSubmit() }
      </form>
    )
  }

  private renderSubmit () {
    const {
      pristine,
      alreadySubmitted,
    } = this.props

    if (this.mode === 'credit-card') {
      return (
        <Button { ...getSaveButtonFormProps(this.props) } submit fullWidth qaMarker='submit-button'>
          Next
        </Button>
      )
    } else if (alreadySubmitted && pristine) {
      return (
        <NavigationLink to={ ONBOARDING_SUMMARY }>
          <Button fullWidth qaMarker='next-button'>
            Next
          </Button>
        </NavigationLink>
      )
    } else {
      return (
        <Button { ...getSaveButtonFormProps(this.props) } submit fullWidth qaMarker='submit-button'>
          Finish
        </Button>
      )
    }
  }

  private get mode () {
    return this.props.mode ?? 'regular'
  }

  componentDidMount () {
    const {
      preloadedCars,
      getCarMakes, getCarModels,
    } = this.props

    this.props.change('form_mode', this.mode)

    getCarMakes()

    preloadedCars.forEach((car) => {
      getCarModels(car.make.id)
    })
  }

  private getHousingTypeFields = (): ReactNode => {
    const { housing_type, onHomeAdded } = this.props

    if (housing_type === null) {
      return null
    }

    const homesArrayField = (
      <FieldArray
        name='homes'
        component={ PropertyList }
        props={{
          ...FIELDS_ARRAY_HOMES_PROPS,
          onFieldAdded: onHomeAdded,
          qaMarker: 'add-home-button',
        }}
      />
    )

    const zipField = (
      <Field
        name='zip5'
        props={ FIELD_ZIP_PROPS }
        component={ TextFieldWrapped }
      />
    )

    return (
      <div className={ cn(styles.innerForm, styles.housingType) }>
        { housing_type === 'ownership' ? homesArrayField : zipField }
      </div>
    )
  }

  private onSubmit: OnSubmitReduxForm = (values: ValidatedPropertyFormValues, dispatch, form) => {
    const newValues = omitBy(values, (_value, key) => {
      if (key === 'homes') {
        return values.housing_type !== 'ownership'
      }
      if (key === 'cars') {
        return values.car_owner === 'no'
      }
      return false
    })
    return onFormSubmit(newValues, dispatch, form)
  }
}

export default PropertyForm
